import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { DateInput } from '@/components/ui/date-input';
import { Label } from '@/components/ui/label';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { getTodayDateString } from '@/lib/utils';
import type { JobStatus } from '../types';

interface JobStatusChangeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (closingComments?: string, completedDate?: string, closedDate?: string) => void;
  targetStatus: JobStatus;
  isLoading: boolean;
}

export function JobStatusChangeDialog({
  open,
  onOpenChange,
  onConfirm,
  targetStatus,
  isLoading,
}: JobStatusChangeDialogProps) {
  const [closingComments, setClosingComments] = useState('');
  const [completedDate, setCompletedDate] = useState('');
  const [closedDate, setClosedDate] = useState(getTodayDateString);
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleConfirm = () => {
    // Clear previous validation error
    setValidationError(null);

    // Validate based on target status
    if (targetStatus === 'Closed' && !closingComments.trim()) {
      setValidationError('Closing comments are required when closing a job');
      return;
    }

    if (targetStatus === 'NoGo' && !closingComments.trim()) {
      setValidationError('Closing comments are required when marking a job as NoGo');
      return;
    }

    if (targetStatus === 'Closed' || targetStatus === 'NoGo') {
      onConfirm(closingComments, completedDate || undefined, closedDate || undefined);
    } else {
      // For Sold (reopening), no extra data needed
      onConfirm();
    }
  };

  const handleCancel = () => {
    // Reset form state
    setClosingComments('');
    setCompletedDate('');
    setClosedDate(getTodayDateString());
    setValidationError(null);
    onOpenChange(false);
  };

  // Reset form when dialog closes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      // Dialog is closing, reset form
      setClosingComments('');
      setCompletedDate('');
      setClosedDate(getTodayDateString());
      setValidationError(null);
    }
    onOpenChange(newOpen);
  };

  const getTitle = () => {
    switch (targetStatus) {
      case 'Closed':
        return 'Close Job';
      case 'NoGo':
        return 'Mark Job as NoGo';
      case 'Sold':
        return 'Reopen Job';
      default:
        return 'Change Job Status';
    }
  };

  const getConfirmText = () => {
    switch (targetStatus) {
      case 'Closed':
        return isLoading ? 'Closing...' : 'Close Job';
      case 'NoGo':
        return isLoading ? 'Marking NoGo...' : 'Mark as NoGo';
      case 'Sold':
        return isLoading ? 'Reopening...' : 'Reopen Job';
      default:
        return isLoading ? 'Updating...' : 'Confirm';
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{getTitle()}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <ErrorAlert error={validationError} />

          {(targetStatus === 'Closed' || targetStatus === 'NoGo') && (
            <>
              <div className="space-y-2">
                <Label htmlFor="closingComments">Closing Comments *</Label>
                <Textarea
                  id="closingComments"
                  value={closingComments}
                  onChange={(e) => setClosingComments(e.target.value)}
                  placeholder={targetStatus === 'NoGo'
                    ? "Enter reason for marking as NoGo..."
                    : "Enter reason for closing the job..."}
                  rows={4}
                  className="text-sm"
                />
              </div>

              {targetStatus === 'Closed' && (
                <div className="space-y-2">
                  <Label htmlFor="completedDate">Completed Date (Optional)</Label>
                  <DateInput
                    id="completedDate"
                    value={completedDate || undefined}
                    onChange={(iso) => setCompletedDate(iso ?? '')}
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="closedDate">{targetStatus === 'NoGo' ? 'NoGo Date' : 'Closed Date'}</Label>
                <DateInput
                  id="closedDate"
                  value={closedDate || undefined}
                  onChange={(iso) => setClosedDate(iso ?? '')}
                />
              </div>
            </>
          )}

          {targetStatus === 'Sold' && (
            <div className="text-sm text-gray-600">
              Are you sure you want to reopen this job? This will change the job status back to
              Sold.
            </div>
          )}

          <div className="flex justify-end gap-2 mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="action"
              onClick={handleConfirm}
              disabled={isLoading}
            >
              {getConfirmText()}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
